import 'reflect-metadata';

import { X509Certificate } from '@peculiar/x509';
import { Convert } from 'pvtsutils';

const CERTIFICATE_BODY_PATTERN = /^[A-Za-z0-9+/]+={0,2}$/;
const PEM_CERTIFICATE_BEGIN_MARKER = '-----BEGIN CERTIFICATE-----';
const PEM_CERTIFICATE_END_MARKER = '-----END CERTIFICATE-----';

export interface SamlCertificateMetadata {
  thumbprint: string;
  subject: string;
  expiryDate: string;
}

const normalizeBase64 = (value: string) => value.replace(/\s+/g, '');

const hasValidBase64Structure = (value: string) => {
  if (!value || value.length % 4 !== 0) return false;
  return CERTIFICATE_BODY_PATTERN.test(value);
};

export const isBase64EncodedPemCertificateContent = (certificate?: string) => {
  const pemBody = extractPemCertificateBody(certificate);
  if (!pemBody) return false;

  return hasValidBase64Structure(normalizeBase64(pemBody));
};

const decodeBase64ToBytes = (value: string) =>
  new Uint8Array(Convert.FromBase64(value));

const formatExpiryDate = (date: Date) => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toHex = (bytes: Uint8Array) =>
  Convert.ToHex(bytes)
    .toUpperCase()
    .replace(/(..)(?=.)/g, '$1:');

const computeSha1 = async (bytes: Uint8Array<ArrayBuffer>) => {
  const digestBuffer = await globalThis.crypto.subtle.digest('SHA-1', bytes);
  return new Uint8Array(digestBuffer);
};

const looksLikeDerSequence = (bytes: Uint8Array) =>
  bytes.length > 0 && bytes[0] === 0x30;

const extractPemCertificateBody = (certificate?: string) => {
  const trimmedCertificate = certificate?.trim();
  if (!trimmedCertificate) return null;

  if (!trimmedCertificate.startsWith(PEM_CERTIFICATE_BEGIN_MARKER)) return null;
  if (!trimmedCertificate.endsWith(PEM_CERTIFICATE_END_MARKER)) return null;

  const body = trimmedCertificate
    .slice(
      PEM_CERTIFICATE_BEGIN_MARKER.length,
      trimmedCertificate.length - PEM_CERTIFICATE_END_MARKER.length
    )
    .trim();

  return body || null;
};

const isValidPemCertificateBody = (value: string) => {
  const normalizedValue = normalizeBase64(value);
  if (!hasValidBase64Structure(normalizedValue)) return false;

  try {
    const bytes = decodeBase64ToBytes(normalizedValue);
    return looksLikeDerSequence(bytes);
  } catch {
    return false;
  }
};

export const validateSamlCertificateContent = (certificate?: string) => {
  const pemBody = extractPemCertificateBody(certificate);
  if (!pemBody) return false;

  return isValidPemCertificateBody(pemBody);
};

export const extractSamlCertificateMetadata = async (
  certificate?: string
): Promise<SamlCertificateMetadata | null> => {
  const pemBody = extractPemCertificateBody(certificate);
  if (!pemBody) return null;

  const normalizedValue = normalizeBase64(pemBody);
  if (!hasValidBase64Structure(normalizedValue)) return null;

  try {
    const certificateData = decodeBase64ToBytes(normalizedValue);
    const parsedCertificate = new X509Certificate(certificateData);

    const subject = parsedCertificate.subjectName.toString().trim();
    const expiryDate = formatExpiryDate(parsedCertificate.notAfter);

    if (!subject || !expiryDate) return null;

    const thumbprintBytes = await computeSha1(certificateData);
    const thumbprint = toHex(thumbprintBytes);

    return {
      thumbprint,
      subject,
      expiryDate,
    };
  } catch {
    return null;
  }
};
